import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismCard,
  PrismInput,
  PrismMessage,
  PrismTable,
  PrismTableCellsTdTitleAttribute,
  PrismToggle,
} from '@mylighthouse/prism-ember';
import type { CustomEmberTableRow } from '@mylighthouse/prism-ember/types/components/prism/table';
import { t } from 'ember-intl';
import GeneralTablesCellsTdEditable from 'lumen/components/general/tables/cells/td-editable';
import SettingsPropertyTagsCellsTdSelectUser from 'lumen/components/settings/property-tags/cells/td-select-user';
import SettingsPropertyTagsTablePropertyTagsBulkEditRow from 'lumen/components/settings/property-tags/table-property-tags/bulk-edit-row';
import type { PropertyTags, PropertyTagsUser } from 'lumen/models';

const COLUMN_PROPERTIES = {
  PROPERTY: 'property',
  OWNERSHIP: 'ownership',
  HOTEL_CLASS: 'hotelClass',
  REGION: 'region',
  REGIONAL_DOS: 'regionalDos',
  REGIONAL_VP: 'regionalVp',
  DORM: 'dorm',
};

export const FREE_TEXT_MAX_LENGTH = 120;

interface SettingsPropertyTagsTablePropertyTagsSignature {
  Args: {
    isLoading: boolean;
    propertyTags: PropertyTags[] | null;
  };
}

export default class SettingsPropertyTagsTablePropertyTags extends Component<SettingsPropertyTagsTablePropertyTagsSignature> {
  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  @tracked hideReadOnlyProperties = false;
  @tracked searchValue = '';
  @tracked selectedRowIds: string[] = [];

  initialSorts = [{ valuePath: COLUMN_PROPERTIES.PROPERTY, isAscending: true }];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    return [
      {
        name: this.intl.t('settings.property_tags.property'),
        valuePath: COLUMN_PROPERTIES.PROPERTY,
        textAlign: 'left' as const,
        minWidth: 200,
        tdComponent: PrismTableCellsTdTitleAttribute,
      },
      {
        name: this.intl.t('settings.property_tags.ownership'),
        valuePath: COLUMN_PROPERTIES.OWNERSHIP,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.OWNERSHIP}`,
        textAlign: 'left' as const,
        tdComponent: GeneralTablesCellsTdEditable,
        maxLength: FREE_TEXT_MAX_LENGTH,
      },
      {
        name: this.intl.t('settings.property_tags.hotel_class'),
        valuePath: COLUMN_PROPERTIES.HOTEL_CLASS,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.HOTEL_CLASS}`,
        textAlign: 'left' as const,
        tdComponent: GeneralTablesCellsTdEditable,
        maxLength: FREE_TEXT_MAX_LENGTH,
      },
      {
        name: this.intl.t('settings.property_tags.region'),
        valuePath: COLUMN_PROPERTIES.REGION,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.REGION}`,
        textAlign: 'left' as const,
        tdComponent: GeneralTablesCellsTdEditable,
        maxLength: FREE_TEXT_MAX_LENGTH,
      },
      {
        name: this.intl.t('settings.property_tags.regional_dos'),
        valuePath: COLUMN_PROPERTIES.REGIONAL_DOS,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.REGIONAL_DOS}`,
        textAlign: 'left' as const,
        tdComponent: SettingsPropertyTagsCellsTdSelectUser,
      },
      {
        name: this.intl.t('settings.property_tags.regional_vp'),
        valuePath: COLUMN_PROPERTIES.REGIONAL_VP,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.REGIONAL_VP}`,
        textAlign: 'left' as const,
        tdComponent: SettingsPropertyTagsCellsTdSelectUser,
      },
      {
        name: this.intl.t('settings.property_tags.dorm'),
        valuePath: COLUMN_PROPERTIES.DORM,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.DORM}`,
        textAlign: 'left' as const,
        tdComponent: SettingsPropertyTagsCellsTdSelectUser,
      },
    ];
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredPropertyTags() {
    if (!this.args.propertyTags) {
      return [];
    }

    let filteredPropertyTags = this.#filterPropertyTagsByReadOnly(
      this.args.propertyTags,
    );
    filteredPropertyTags =
      this.#filterPropertyTagsBySearch(filteredPropertyTags);

    return filteredPropertyTags;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasReadOnlyPropertyTags() {
    if (!this.args.propertyTags) {
      return false;
    }

    return this.args.propertyTags.some((propertyTag) => propertyTag.isReadOnly);
  }

  // Preserve reference for equality check in ember-table
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    const missingValue = this.intl.t('general.missing');

    return this.filteredPropertyTags.map((propertyTag) => {
      // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
      const isSelected = this.selectedRowIds.includes(propertyTag.id);

      return {
        id: propertyTag.id,
        propertyTag,

        disableCheckboxSelection: propertyTag.isReadOnly,
        isReadOnly: propertyTag.isReadOnly,
        isDisabled: isSelected,

        [COLUMN_PROPERTIES.PROPERTY]: propertyTag.subscription.name,
        [COLUMN_PROPERTIES.OWNERSHIP]: propertyTag.ownership,
        [COLUMN_PROPERTIES.HOTEL_CLASS]: propertyTag.hotelClass,
        [COLUMN_PROPERTIES.REGION]: propertyTag.region,
        [COLUMN_PROPERTIES.REGIONAL_DOS]:
          propertyTag.regionalDos?.name ?? missingValue,
        [COLUMN_PROPERTIES.REGIONAL_VP]:
          propertyTag.regionalVp?.name ?? missingValue,
        [COLUMN_PROPERTIES.DORM]: propertyTag.dorm?.name ?? missingValue,

        displayValue: {
          [COLUMN_PROPERTIES.OWNERSHIP]: {
            value: propertyTag.ownership ?? missingValue,
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            onEdit: (value: string | null) => {
              propertyTag.ownership = value || null;
              this.showToastIfPropertyTagsHiddenOnEdit(propertyTag);
            },
          },
          [COLUMN_PROPERTIES.HOTEL_CLASS]: {
            value: propertyTag.hotelClass ?? missingValue,
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            onEdit: (value: string | null) => {
              propertyTag.hotelClass = value || null;
              this.showToastIfPropertyTagsHiddenOnEdit(propertyTag);
            },
          },
          [COLUMN_PROPERTIES.REGION]: {
            value: propertyTag.region ?? missingValue,
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            onEdit: (value: string | null) => {
              propertyTag.region = value || null;
              this.showToastIfPropertyTagsHiddenOnEdit(propertyTag);
            },
          },
          [COLUMN_PROPERTIES.REGIONAL_DOS]: {
            user: propertyTag.regionalDos,
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            onEdit: (user: PropertyTagsUser) => {
              propertyTag.regionalDos = user;
              this.showToastIfPropertyTagsHiddenOnEdit(propertyTag);
            },
            subscriptionIds: [propertyTag.subscription.id],
          },
          [COLUMN_PROPERTIES.REGIONAL_VP]: {
            user: propertyTag.regionalVp,
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            onEdit: (user: PropertyTagsUser) => {
              propertyTag.regionalVp = user;
              this.showToastIfPropertyTagsHiddenOnEdit(propertyTag);
            },
            subscriptionIds: [propertyTag.subscription.id],
          },
          [COLUMN_PROPERTIES.DORM]: {
            user: propertyTag.dorm,
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            onEdit: (user: PropertyTagsUser) => {
              propertyTag.dorm = user;
              this.showToastIfPropertyTagsHiddenOnEdit(propertyTag);
            },
            subscriptionIds: [propertyTag.subscription.id],
          },
        },
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPropertyTags() {
    return this.selectedRows.map((row) => row.propertyTag);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRows() {
    // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
    return this.rows.filter((row) => this.selectedRowIds.includes(row.id));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  #filterPropertyTagsByReadOnly(propertyTags: PropertyTags[]) {
    if (!this.hideReadOnlyProperties) {
      return propertyTags;
    }

    return propertyTags.filter((propertyTag) => !propertyTag.isReadOnly);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  #filterPropertyTagsBySearch(propertyTags: PropertyTags[]) {
    if (!this.searchValue) {
      return propertyTags;
    }

    const query = this.searchValue.toLowerCase();

    return propertyTags.filter((propertyTag) => {
      const searchableFields = [
        propertyTag.subscription.name,
        propertyTag.ownership,
        propertyTag.hotelClass,
        propertyTag.region,
        propertyTag.regionalDos?.name,
        propertyTag.regionalVp?.name,
        propertyTag.dorm?.name,
      ];

      return searchableFields.some((value) => {
        if (value === null || value === undefined) {
          return false;
        }

        return value.toLowerCase().includes(query);
      });
    });
  }

  @action bulkEditFreeTextField(
    field: 'ownership' | 'hotelClass' | 'region',
    value: string | null,
  ): void {
    if (value === null) {
      return;
    }

    const { selectedPropertyTags } = this;

    selectedPropertyTags.forEach((propertyTag) => {
      propertyTag[field] = value || null;
    });

    this.showToastIfPropertyTagsHiddenOnEdit(selectedPropertyTags);
    this.removeHiddenRowsFromRowSelection();
  }

  @action bulkEditUserField(
    field: 'regionalDos' | 'regionalVp' | 'dorm',
    user: PropertyTagsUser | null,
  ): void {
    const { selectedPropertyTags } = this;

    selectedPropertyTags.forEach((propertyTag) => {
      propertyTag[field] = user;
    });

    this.showToastIfPropertyTagsHiddenOnEdit(selectedPropertyTags);
    this.removeHiddenRowsFromRowSelection();
  }

  @action
  async clearSelectedPropertyTags(): Promise<void> {
    const isClearConfirmed = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('settings.property_tags.clear.modal.title'),
      text: this.intl.t('settings.property_tags.clear.modal.description'),
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t('settings.property_tags.clear.modal.cta.confirm'),
        },
      },
    });

    if (!isClearConfirmed) {
      return;
    }

    const { selectedPropertyTags } = this;

    selectedPropertyTags.forEach((propertyTag) => {
      propertyTag.resetClearableFields();
    });

    this.showToastIfPropertyTagsHiddenOnEdit(selectedPropertyTags);
    this.removeHiddenRowsFromRowSelection();
  }

  /**
   * In some cases, a selected rows might be hidden due to search filtering.
   * This method ensures that the selected rows array only contains visible rows.
   */
  @action removeHiddenRowsFromRowSelection(): void {
    // @ts-expect-error: Type '(string | null)[]' is not assignable to type 'string[]'.
    this.selectedRowIds = this.selectedRows.map((row) => row.id);
  }

  @action setSelectedRows(
    rows: CustomEmberTableRow | CustomEmberTableRow[],
  ): void {
    if (!Array.isArray(rows)) {
      return;
    }

    this.selectedRowIds = rows.map((row) => row['id'] as string);
  }

  @action showToastIfPropertyTagsHiddenOnEdit(
    propertyTags: PropertyTags | PropertyTags[],
  ): void {
    const tags = Array.isArray(propertyTags) ? propertyTags : [propertyTags];

    const isAnyTagHidden = tags.some(
      (propertyTag) =>
        !this.filteredPropertyTags.find((tag) => tag.id === propertyTag.id),
    );

    if (isAnyTagHidden) {
      this.toast.success(
        this.intl.t('settings.property_tags.edit.toast.hid_row'),
        {
          showIcon: true,
        },
      );
    }
  }

  @action updateSearchValue(event: Event): void {
    this.searchValue = (event.target as HTMLInputElement).value;
    this.removeHiddenRowsFromRowSelection();
  }

  <template>
    <PrismCard @contentLayout="noPadding" class="h-full overflow-hidden">
      <:header>
        {{#if @propertyTags}}
          <span class="text-200--regular" data-test-id="hotel-count">
            {{t "general.hotel.count" count=this.filteredPropertyTags.length}}
          </span>
        {{else if @isLoading}}
          <div class="loading-pulse w-[52px]"></div>
        {{/if}}
      </:header>
      <:headerAfter>
        <div class="flex items-center gap-200">
          {{#if this.hasReadOnlyPropertyTags}}
            <PrismToggle
              @dataTestId="toggle-hide-read-only-properties"
              @isChecked={{this.hideReadOnlyProperties}}
              @label={{t "settings.property_tags.hide_read_only"}}
              @onChange={{fn (mut this.hideReadOnlyProperties)}}
            />
          {{/if}}
          <PrismInput
            @onInput={{this.updateSearchValue}}
            @placeholder={{t "settings.property_tags.search.placeholder"}}
            @removePadding={{true}}
            @value={{this.searchValue}}
            data-test-id="input-search"
          />
        </div>
      </:headerAfter>
      <:content>
        <PrismTable
          @checkboxSelectionMode="multiple"
          @columns={{this.columns}}
          @disabledCheckboxIcon="lock"
          @disabledCheckboxTooltipText={{t
            "settings.property_tags.read_only_explanation"
          }}
          @enableAutoSortsUpdate={{true}}
          @initialSorts={{this.initialSorts}}
          @isLoading={{@isLoading}}
          @onRowSelect={{this.setSelectedRows}}
          @oneCellBulkEditRow={{false}}
          @removeTableBorder={{true}}
          @renderAll={{false}}
          @rowSelection={{this.selectedRows}}
          @rows={{this.rows}}
          data-test-id="table-property-tags"
        >
          <:bulkEditRow>
            <SettingsPropertyTagsTablePropertyTagsBulkEditRow
              @clearSelected={{this.clearSelectedPropertyTags}}
              @selectedPropertyTags={{this.selectedPropertyTags}}
              @setDorm={{fn this.bulkEditUserField "dorm"}}
              @setHotelClass={{fn this.bulkEditFreeTextField "hotelClass"}}
              @setOwnership={{fn this.bulkEditFreeTextField "ownership"}}
              @setRegion={{fn this.bulkEditFreeTextField "region"}}
              @setRegionalDos={{fn this.bulkEditUserField "regionalDos"}}
              @setRegionalVp={{fn this.bulkEditUserField "regionalVp"}}
            />
          </:bulkEditRow>
          <:emptyState>
            <div class="flex h-full w-full items-center justify-center">
              <PrismMessage
                @description=""
                @illustration="100x100_search"
                @title={{t "settings.property_tags.empty_state.title"}}
                data-test-id="empty-state"
              />
            </div>
          </:emptyState>
        </PrismTable>
      </:content>
    </PrismCard>
  </template>
}
